import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { DoctorList, PaginatedDoctorList } from '@/types/settings-api';

/**
 * Practitioner (doctor) list for the Practitioner tab (PROD-760).
 *
 * `GET /api/v1/Practice/{organizationId}/PractitionerRoles?role=doctor&showArchived={bool}`
 *
 * Flutter source: `settings_data_source_impl.dart:499-518` (`fetchDoctorList`).
 * Two non-obvious wire details:
 *
 * 1. Path lives under `/api/v1/Practice/{orgId}/PractitionerRoles` — NOT
 *    `/api/Doctor/PractitionerRoles` (the original React URL was a REST
 *    guess that 404s on dev).
 * 2. Response is a paginated wrapper (`PaginatedDoctorList`) — the actual
 *    rows are under `.results`, not a bare array. Hook unwraps to keep
 *    consumers simple.
 *
 * Fixed 2026-05-12 alongside the roles + practices URL drift.
 */
export function useDoctorsList(
  organizationId: string | null | undefined,
  showArchived: boolean,
) {
  return useQuery({
    queryKey: ['settings', 'doctors-list', organizationId, showArchived],
    queryFn: async ({ signal }) => {
      if (!organizationId) throw new Error('useDoctorsList called without orgId');
      const res = await get<PaginatedDoctorList>(
        `/api/v1/Practice/${organizationId}/PractitionerRoles?role=doctor&showArchived=${showArchived}`,
        { signal },
      );
      return (res.results ?? []) as DoctorList[];
    },
    enabled: !!organizationId,
  });
}
